/**
 * SquareHunt Mini-Game
 *
 * Self-contained game: Engine + Definition + Registration
 *
 * Rules:
 * - Empty board, no pieces
 * - Player is told the name(s) of target squares
 * - Player clicks on the named square(s)
 * - Correct clicks are highlighted
 * - Win: All target squares clicked
 * - Fail: Click limit exceeded (if set)
 */
import { MiniGameBase } from "../MiniGameBase";
import { MiniGameRegistry } from "../MiniGameRegistry";
// ========================================
// Engine
// ========================================
export class SquareHuntEngine extends MiniGameBase {
    constructor(config) {
        super(config);
        // Ensure these arrays exist for Vue reactivity
        this.state.foundSquares = [];
        this.state.wrongSquares = [];
    }
    /**
     * No piece movement in SquareHunt.
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
    getValidMoves(_square) {
        return [];
    }
    /**
     * Handle a move (which is really a click for SquareHunt).
     * For compatibility, `from === to` indicates a click.
     */
    makeMove(from, to) {
        // SquareHunt uses clicks, not drags
        if (from !== to) {
            return {
                valid: false,
                error: "SquareHunt uses clicks, not piece movement",
            };
        }
        return this.handleClick(to);
    }
    /**
     * Handle a square click.
     */
    handleClick(square) {
        if (this.state.status !== "playing") {
            return { valid: false, error: "Game is already over" };
        }
        // Check if already found
        if (this.state.foundSquares.includes(square)) {
            return { valid: true, targetHit: false };
        }
        const isTarget = this.state.remainingTargets.includes(square);
        // Update move count
        this.state.moveCount++;
        this.state.moves.push({ from: square, to: square });
        if (isTarget) {
            // Correct click - add to found squares and remove from targets
            // Use array spread to ensure Vue reactivity picks up the change
            this.state.foundSquares = [...this.state.foundSquares, square];
            this.state.targetsAchieved++;
            this.state.remainingTargets = this.state.remainingTargets.filter((t) => t !== square);
            // Check win
            if (this.state.remainingTargets.length === 0) {
                this.state.status = "complete";
                return {
                    valid: true,
                    targetHit: true,
                    targetSquare: square,
                    gameComplete: true,
                };
            }
            return {
                valid: true,
                targetHit: true,
                targetSquare: square,
            };
        }
        else {
            // Wrong click - add to wrong squares for red highlighting
            if (!this.state.wrongSquares.includes(square)) {
                // Use array spread to ensure Vue reactivity picks up the change
                this.state.wrongSquares = [...this.state.wrongSquares, square];
            }
            // Check fail
            if (this.isMoveLimitExceeded()) {
                this.state.status = "failed";
                return {
                    valid: true,
                    targetHit: false,
                    gameFailed: true,
                    error: "Too many clicks! Game over.",
                };
            }
            return {
                valid: true,
                targetHit: false,
                error: "Wrong square! Try again.",
            };
        }
    }
    /**
     * Get a hint - return a random remaining target.
     */
    getHint() {
        if (this.state.status !== "playing")
            return null;
        if (this.state.remainingTargets.length === 0)
            return null;
        const randomIndex = Math.floor(Math.random() * this.state.remainingTargets.length);
        const target = this.state.remainingTargets[randomIndex];
        // For click-based games, from === to
        return { from: target, to: target };
    }
}
// ========================================
// Registration
// ========================================
MiniGameRegistry.register({
    type: "SQUARE_HUNT",
    createEngine: (config) => new SquareHuntEngine(config),
    validate: (config) => {
        if (!config.targets || config.targets.length === 0) {
            return {
                valid: false,
                errors: [
                    "SquareHunt requires target squares (use target=e4 or targets=e4,d5 in DSL)",
                ],
            };
        }
        return { valid: true, errors: [] };
    },
    getInstructionText: (config) => {
        const targets = config.targets || [];
        if (targets.length === 0) {
            return "No target squares specified.";
        }
        if (targets.length === 1) {
            return `Click on square ${targets[0].toUpperCase()}`;
        }
        // Format multiple squares: "a1, b2, and c3"
        const upperTargets = targets.map((t) => t.toUpperCase());
        if (upperTargets.length === 2) {
            return `Click on squares ${upperTargets[0]} and ${upperTargets[1]}`;
        }
        const last = upperTargets.pop();
        return `Click on squares ${upperTargets.join(", ")}, and ${last}`;
    },
    supportsHints: true,
    interactionType: "click",
});
